import { useSeo } from '../lib/seo';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, LifeBuoy, Mail, MapPin, Megaphone, Newspaper, Trophy, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import api from '../lib/api';
import MembershipRenewalModal from '../components/MembershipRenewalModal';
import PartnerWelcomeBanner from '../components/PartnerWelcomeBanner';
import { mergeFreeEntry } from '../lib/siteCopy';

/**
 * Trade / Partners landing page — content migrated from the existing
 * goleicestershire.com /trade/index page. Foundation Phase 1: static
 * content faithful to the live site. Phase 2 will add Stripe-backed
 * ticket purchase + membership renewal flows on top of this skeleton.
 */

const TIERS = [
  {
    name: 'Entry Level',
    price: '£200',
    suffix: '+ VAT per year',
    tagline: 'Perfect for new businesses getting to know us',
    benefits: [
      'Basic listing on GoLeicestershire — name, address, phone, email, website link, 1 image, 50-word description',
      'Listings in our weekly consumer newsletter to ~10,000 local contacts',
      'Occasional posts on social media shared through LPL platforms',
      'Access to research and insights',
      'Advice & Support hub — helpline for queries and toolkits (1 working-day response)',
    ],
  },
  {
    name: 'The Full Package',
    price: '£700',
    suffix: '+ VAT per year',
    tagline: 'A broad yet focused package designed to maximise impact',
    highlight: true,
    benefits: [
      'Single panel entry in the Great Days Out Visitor Guide (artwork supplied)',
      'Enhanced listing on GoLeicestershire — unlimited images, unlimited word count, social-media links',
      'Discounted tickets for industry events including Tourism Awards & Tourism Forum',
      'Dedicated posts through our social media channels',
      'Annual review of online presence with headline feedback',
      '1 feature in weekly consumer e-newsletter to ~10,000 local contacts',
      '"First refusal" for marketing campaigns including seasonal promotions',
      'Inclusion in fam visits + information on funding opportunities',
      'Workshop for entering the Tourism Awards',
      'Priority Advice & Support — direct line to Janine and Martin for strategy questions',
    ],
  },
  {
    name: 'The Gold Standard',
    price: '£1,325',
    suffix: '+ VAT per year',
    tagline: 'How much more could we squeeze in?',
    benefits: [
      'Double panel entry in the Visitor Guide',
      'Guaranteed front cover presence',
      'Enhanced listing on GoLeicestershire (unlimited content)',
      'Guaranteed homepage feature for at least 3 months',
      'Discounted tickets for industry events',
      'Priority campaign access including StayPlayExplore short breaks',
      'Annual "mystery shop" with Hotel-Inspector-style feedback (if requested)',
      '4 hours 1-to-1 business development & marketing support',
      'One front-page feature a year in our weekly consumer e-newsletter',
      'Ability to run competitions through our social media platforms',
      'Workshop for entering the Tourism Awards',
      'Bespoke Advice & Support — quarterly strategy call + early sight of new grants and national toolkits',
    ],
  },
];

export default function TradePartnersPage() {
  useSeo(
    'Trade Partners Hub | Go Leicestershire',
    'Resources, membership and marketing opportunities for Leicestershire tourism businesses — the Go Leicestershire trade partners hub.'
  );
  const [enquiryTier, setEnquiryTier] = useState(null); // null = closed; string = open + prefilled tier
  const [renewOpen, setRenewOpen] = useState(false);
  const [freeEntry, setFreeEntry] = useState(mergeFreeEntry(null));
  useEffect(() => {
    api.get('/site-settings/free_entry_note')
      .then((r) => setFreeEntry(mergeFreeEntry(r.data?.value)))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-base" data-testid="trade-partners-page">
      <Helmet>
        <title>Trade Partners — GoLeicestershire</title>
        <meta name="description" content="Become a Leicestershire Promotions partner: visitor guide advertising, dedicated marketing support, Tourism Awards & more." />
      </Helmet>

      {enquiryTier !== null && (
        <EnquiryModal initialTier={enquiryTier} onClose={() => setEnquiryTier(null)} />
      )}
      <MembershipRenewalModal open={renewOpen} onClose={() => setRenewOpen(false)} />

      <PartnerWelcomeBanner />

      <SectionNav />

      {/* Hero */}
      <section className="bg-heritage text-base py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
          <div className="label text-mustard mb-4">For tourism businesses</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mb-6 max-w-3xl">
            Trade <em className="not-italic text-mustard">Partners</em>
          </h1>
          <p className="text-base/85 text-lg md:text-xl leading-relaxed max-w-2xl mb-8">
            Three ways to put your venue, attraction or experience in front of more visitors —
            backed by 35+ years of running tourism marketing for Leicester and Leicestershire.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <a href="#tiers" className="inline-flex items-center gap-1.5 bg-mustard text-charcoal font-semibold rounded-full px-5 py-2.5 hover:bg-base transition-colors" data-testid="hero-cta-tiers">
              Partnership tiers
            </a>
            <a href="#marketing" className="inline-flex items-center gap-1.5 border border-base/30 text-base font-semibold rounded-full px-5 py-2.5 hover:bg-base/10 transition-colors" data-testid="hero-cta-marketing">
              Marketing channels
            </a>
            <a href="#awards" className="inline-flex items-center gap-1.5 border border-base/30 text-base font-semibold rounded-full px-5 py-2.5 hover:bg-base/10 transition-colors" data-testid="hero-cta-awards">
              Tourism Awards
            </a>
            <a href="#support" className="inline-flex items-center gap-1.5 border border-base/30 text-base font-semibold rounded-full px-5 py-2.5 hover:bg-base/10 transition-colors" data-testid="hero-cta-support">
              Advice &amp; support
            </a>
            <a href="/partners/my-listing" className="inline-flex items-center gap-1.5 bg-base text-heritage font-semibold rounded-full px-5 py-2.5 hover:bg-mustard transition-colors" data-testid="hero-cta-my-listing">
              Manage your listing →
            </a>
            <a href="/partners/my-listing#submit-event" className="inline-flex items-center gap-1.5 bg-base text-heritage font-semibold rounded-full px-5 py-2.5 hover:bg-mustard transition-colors" data-testid="hero-cta-submit-event">
              Submit an event →
            </a>
            <a href="/partners/guide" className="inline-flex items-center gap-1.5 bg-base text-heritage font-semibold rounded-full px-5 py-2.5 hover:bg-mustard transition-colors" data-testid="hero-cta-guide">
              Getting started guide →
            </a>
          </div>
        </div>
      </section>

      {/* ─── Tiers ─── */}
      <section id="tiers" className="bg-charcoal text-base py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="label text-mustard mb-3">01 · Partnership tiers</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-4">Choose how hard you want us to work for you</h2>
            <p className="text-base/70 max-w-2xl mx-auto leading-relaxed">
              From a simple directory listing to the full Gold Standard — annual partnership packages with everything you need to be discovered by Leicestershire visitors.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {TIERS.map((tier) => (
              <div
                key={tier.name}
                className={`rounded-2xl p-6 md:p-8 flex flex-col ${tier.highlight ? 'bg-mustard text-charcoal' : 'bg-base/10 border border-base/20'}`}
                data-testid={`tier-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="font-display text-2xl mb-2">{tier.name}</div>
                <div className={`text-sm mb-5 ${tier.highlight ? 'text-charcoal/75' : 'text-base/70'}`}>{tier.tagline}</div>
                <div className="mb-6">
                  <span className="text-4xl font-display">{tier.price}</span>
                  <span className={`text-sm ml-2 ${tier.highlight ? 'text-charcoal/65' : 'text-base/60'}`}>{tier.suffix}</span>
                </div>
                <ul className="space-y-2.5 mb-7 flex-1">
                  {tier.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm leading-relaxed">
                      <Check size={14} className={`shrink-0 mt-1 ${tier.highlight ? 'text-heritage' : 'text-mustard'}`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setEnquiryTier(tier.name)}
                  data-testid={`tier-cta-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${tier.highlight ? 'bg-charcoal text-base hover:bg-charcoal/85' : 'bg-base text-charcoal hover:bg-mustard'}`}
                >
                  Enquire about {tier.name} <ArrowUpRight size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="text-center mt-10 text-sm text-base/60">
            Already agreed a tier? <button type="button" onClick={() => setRenewOpen(true)} data-testid="membership-renew-btn"
              className="text-mustard underline font-semibold">Renew or pay online</button>. Prefer a direct conversation? Email{' '}
            <a href="mailto:janine.williams@l-p-l.com" className="text-mustard underline">janine.williams@l-p-l.com</a>.
          </div>
          <div className="mt-8 bg-base/10 border border-base/20 rounded-2xl p-6 md:p-7 max-w-3xl mx-auto text-center" data-testid="free-entry-note">
            <div className="font-display text-xl mb-2">{freeEntry.title}</div>
            <p className="text-base/70 text-sm leading-relaxed">{freeEntry.body}</p>
          </div>
        </div>
      </section>

      {/* ─── Marketing channels ─── */}
      <section id="marketing" className="py-16 md:py-20 max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="mb-10">
          <div className="label text-heritage mb-3">02 · Marketing channels</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-4">Get in front of visitors at scale</h2>
          <p className="text-charcoal/75 max-w-2xl leading-relaxed">
            Beyond annual partnership, we run three standout marketing channels that put your business
            in front of millions of visitors a year — open to all partners as add-ons.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          <SectionCard
            icon={Newspaper}
            kicker="Print + digital"
            title="Great Days Out Visitor Guide"
            body="The only printed Visitor Guide to the city &amp; county — distributed by Pear to 550,000 residents and 25 million national impressions. Every advertiser also gets a goleicestershire.com listing."
            cta="Distribution &amp; advertising details"
            to="/partners/great-days-out"
          />
          <SectionCard
            icon={Megaphone}
            kicker="Family short breaks"
            title="StayPlayExplore"
            body="Curated family-friendly hotel + attractions bundles that stay £169 all year round. National marketing campaign with guaranteed inclusion for partners."
            cta="About the campaign"
            to="/partners/stayplayexplore"
          />
          <SectionCard
            icon={Megaphone}
            kicker="Adults-only short breaks"
            title="The Perfect Break"
            body="Adults-only fixed-price breaks from £169 for two — hotel, breakfast and hand-picked experiences bundled and sold at theperfectbreak.co.uk. Guaranteed profiling for partner hotels and experiences."
            cta="About the campaign"
            to="/partners/the-perfect-break"
          />
        </div>
      </section>

      {/* ─── Tourism Awards ─── */}
      <section id="awards" className="bg-stone py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="mb-10">
            <div className="label text-heritage mb-3">03 · Tourism Awards</div>
            <h2 className="font-display text-3xl sm:text-4xl mb-4">Leicestershire Promotions Tourism &amp; Hospitality Awards</h2>
            <p className="text-charcoal/75 max-w-2xl leading-relaxed">
              The longest-running tourism awards in the county. Enter your business, sponsor a
              category, or buy tickets for the November gala at the National Space Centre.
              The full hub includes category info, eligibility &amp; judging criteria, T&amp;Cs, and last
              year&apos;s winners.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
            <div className="md:col-span-2">
              <SectionCard
                icon={Trophy}
                kicker="2026 entries open now"
                title="Tourism Awards 2026"
                body="Categories, timetable &amp; how to enter the 2026 awards — closing 11 September. Includes tabbed access to <strong>Eligibility &amp; criteria</strong>, <strong>Terms &amp; conditions</strong> and the <strong>2025 winners</strong>."
                cta="Open the Awards hub"
                to="/partners/tourism-awards"
              />
            </div>
            <a
              href="https://visitengland-chapters.secure-platform.com/a/organizations/LEI/home"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="awards-portal-card"
              className="block bg-heritage text-base rounded-2xl p-7 md:p-8 hover:bg-charcoal transition-colors flex flex-col justify-between"
            >
              <div>
                <div className="label text-mustard mb-2 text-[11px]">Ready to enter?</div>
                <h3 className="font-display text-2xl mb-3">Start an application</h3>
                <p className="text-base/85 text-sm leading-relaxed">
                  Submit via the official VisitEngland Awards portal.
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-mustard text-sm font-semibold mt-6">
                Open the portal <ArrowUpRight size={14} />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* ─── Advice, guidance & support ─── */}
      <section id="support" className="py-16 md:py-20 max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="label text-heritage mb-3">04 · Advice, guidance &amp; support</div>
            <h2 className="font-display text-3xl sm:text-4xl mb-4">We&apos;re your first point of contact</h2>
            <p className="text-charcoal/75 leading-relaxed mb-4">
              Accessibility improvements, regenerative tourism plans, visitor data, marketing toolkits —
              we work with VisitEngland, VisitBritain and the regional DMOs every week, so we already know
              which toolkit answers your question and what local funding you should know about.
            </p>
            <p className="text-charcoal/75 leading-relaxed">
              Email us first. We&apos;ll point you straight to the right answer — and where the
              national bodies have the definitive resource, our hub links directly to it.
            </p>
          </div>
          <div className="lg:col-span-5">
            <SectionCard
              icon={LifeBuoy}
              kicker="Curated resources + direct contact"
              title="Open the advice &amp; support hub"
              body="Accessibility · Regenerative tourism · Visitor data &amp; insights · Marketing toolkits · Funding &amp; grants."
              cta="Browse the hub"
              to="/partners/advice-guidance"
            />
          </div>
        </div>
      </section>

      {/* ─── About + Get in touch ─── */}
      <section className="bg-charcoal text-base py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-start">
          <div>
            <div className="label text-mustard mb-3">About us</div>
            <h2 className="font-display text-3xl sm:text-4xl mb-5">Tourism specialists since 1989</h2>
            <p className="text-base/80 leading-relaxed mb-4">
              LPL is a tourism specialist delivering campaigns, events and services to develop tourism
              offers and attract more visitors to a growing portfolio of leading destinations.
            </p>
            <p className="text-base/80 leading-relaxed mb-4">
              Having evolved from the organisation that has managed strategic and operational tourism in
              Leicester and Leicestershire since 1989 (under the name Leicester Promotions Ltd),
              the company now supports a growing range of tourism destinations across the country.
            </p>
            <p className="text-base/80 leading-relaxed">
              We&apos;ve worked with Lincolnshire, Staffordshire, Rutland, Bedfordshire, Birmingham,
              Coventry &amp; Warwickshire, and Yorkshire — in addition to our flagship Leicestershire programme.
            </p>
          </div>

          <div className="bg-base text-charcoal border border-base/20 rounded-2xl p-6 md:p-8">
            <h3 className="font-display text-2xl mb-5">Get in touch</h3>
            <ContactRow icon={Mail} label="Partnerships — Janine Williams, Director of Marketing" value="janine.williams@l-p-l.com" href="mailto:janine.williams@l-p-l.com" />
            <ContactRow icon={Mail} label="Strategy & consultancy — Martin Peters, CEO" value="martin.peters@l-p-l.com" href="mailto:martin.peters@l-p-l.com" />
            <ContactRow icon={Mail} label="General enquiries" value="info@goleicestershire.com" href="mailto:info@goleicestershire.com" />
            <ContactRow icon={MapPin} label="Office" value={<span>2 High Street<br />Loughborough<br />Leicestershire LE11 2PY</span>} />
            <div className="text-xs text-charcoal/55 mt-6 pt-6 border-t border-charcoal/10">
              Leicester Shire Promotions Limited · Company no. 2785317 · Registered in the United Kingdom
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * Sticky in-page nav for the /partners landing page. Appears once the
 * visitor scrolls past the hero and highlights the section currently in
 * view using IntersectionObserver. Hidden on small screens to avoid
 * cluttering the mobile layout (visitors there get the hero quick-links).
 */
const SECTION_NAV = [
  { id: 'tiers', num: '01', label: 'Tiers' },
  { id: 'marketing', num: '02', label: 'Marketing' },
  { id: 'awards', num: '03', label: 'Awards' },
  { id: 'support', num: '04', label: 'Support' },
];

function SectionNav() {
  const [active, setActive] = useState('tiers');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const sentinel = document.querySelector('section.bg-heritage');
    if (!sentinel) return undefined;
    // Reveal the nav once the hero is fully scrolled past.
    const heroObs = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-90px 0px 0px 0px' },
    );
    heroObs.observe(sentinel);
    return () => heroObs.disconnect();
  }, []);

  useEffect(() => {
    // Track which numbered section is in view. We pick the one whose top
    // crosses the upper third of the viewport — gives the nav a clear
    // "you're here" feel rather than flickering at every section edge.
    const ids = SECTION_NAV.map((s) => s.id);
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (sections.length === 0) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((e) => e.isIntersecting);
        if (visibleEntries.length === 0) return;
        // Pick the closest-to-top of the visible sections.
        visibleEntries.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActive(visibleEntries[0].target.id);
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: 0 },
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <div
      data-testid="trade-section-nav"
      className={`hidden md:block sticky top-[64px] z-30 transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
      }`}
      aria-hidden={!visible}
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 -mb-3">
        <nav
          className="bg-charcoal text-base rounded-full shadow-lg px-2 py-2 flex items-center gap-1 w-fit mx-auto"
          aria-label="Trade Partners section navigation"
        >
          {SECTION_NAV.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`section-nav-${s.id}`}
              aria-current={active === s.id ? 'true' : undefined}
              className={`inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-1.5 transition-colors ${
                active === s.id
                  ? 'bg-mustard text-charcoal'
                  : 'text-base/85 hover:text-mustard'
              }`}
            >
              <span className={`text-xs ${active === s.id ? 'text-charcoal/70' : 'text-base/55'}`}>{s.num}</span>
              {s.label}
            </a>
          ))}
        </nav>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  const Inner = href ? 'a' : 'div';
  return (
    <div className="flex items-start gap-3 mb-4 last:mb-0">
      <Icon size={16} className="text-heritage shrink-0 mt-1" />
      <div>
        <div className="text-xs uppercase tracking-wider text-charcoal/55">{label}</div>
        <Inner href={href} className={`text-sm text-charcoal ${href ? 'hover:text-heritage hover:underline' : ''}`}>{value}</Inner>
      </div>
    </div>
  );
}

function EnquiryModal({ initialTier, onClose }) {
  const [name, setName] = useState('');
  const [business, setBusiness] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [tier, setTier] = useState(initialTier || 'General enquiry');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await api.post('/partners/enquire', { name, business, email, phone, tier, message });
      setSent(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Sorry, something went wrong. Please try again or email janine.williams@l-p-l.com directly.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="enquiry-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-base rounded-2xl max-w-lg w-full p-6 md:p-8 my-8 relative">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="enquiry-close"
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-stone"
        >
          <X size={18} />
        </button>
        {sent ? (
          <div className="text-center py-6" data-testid="enquiry-sent">
            <Check size={36} className="text-heritage mx-auto mb-3" />
            <h3 className="font-display text-2xl mb-2">Thanks — we&apos;ve got it</h3>
            <p className="text-charcoal/70 text-sm leading-relaxed mb-5">
              Your enquiry is in Janine&apos;s inbox. She&apos;ll be in touch within a working day or two.
            </p>
            <button type="button" onClick={onClose} className="text-sm font-semibold text-heritage hover:underline">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="enquiry-form" className="space-y-4">
            <div>
              <h3 className="font-display text-2xl mb-1">Partnership enquiry</h3>
              <p className="text-sm text-charcoal/65">Fill this in and we&apos;ll come back to you within a working day or two.</p>
            </div>
            <Field label="Your name" value={name} onChange={setName} testid="enquiry-name" required />
            <Field label="Business name" value={business} onChange={setBusiness} testid="enquiry-business" />
            <Field label="Email" type="email" value={email} onChange={setEmail} testid="enquiry-email" required />
            <Field label="Phone (optional)" type="tel" value={phone} onChange={setPhone} testid="enquiry-phone" />
            <div>
              <label className="block text-xs uppercase tracking-wider text-charcoal/55 mb-1">Interested in</label>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                data-testid="enquiry-tier"
                className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2.5 text-sm"
              >
                <option>General enquiry</option>
                <option>Entry Level</option>
                <option>The Full Package</option>
                <option>The Gold Standard</option>
                <option>Tourism Awards</option>
                <option>Awards ceremony tickets</option>
                <option>Sponsorship</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-charcoal/55 mb-1">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={4}
                maxLength={4000}
                data-testid="enquiry-message"
                className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2.5 text-sm"
                placeholder="Tell us a bit about your business and what you&apos;re looking for…"
              />
            </div>
            {err && <div className="text-sm text-red-700" data-testid="enquiry-error">{err}</div>}
            <button
              type="submit"
              disabled={busy}
              data-testid="enquiry-submit"
              className="w-full bg-heritage text-base font-semibold rounded-full py-3 hover:bg-charcoal disabled:opacity-50 transition-colors"
            >
              {busy ? 'Sending\u2026' : 'Send enquiry'}
            </button>
            <div className="text-xs text-charcoal/50 text-center">
              We&apos;ll only use your details to reply to this enquiry.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testid, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-charcoal/55 mb-1">
        {label}{required && <span className="text-heritage ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2.5 text-sm"
      />
    </div>
  );
}

function ResourceCard({ to, title, body, featured }) {
  return (
    <Link
      to={to}
      data-testid={`resource-card-${to.split('/').pop()}`}
      className={`block rounded-2xl p-6 hover:shadow-sm transition-all ${
        featured
          ? 'bg-heritage text-base border border-heritage hover:bg-charcoal'
          : 'bg-base border border-charcoal/10 hover:border-heritage'
      }`}
    >
      <h3 className={`font-display text-xl mb-2 ${featured ? 'text-base' : 'text-charcoal'}`} dangerouslySetInnerHTML={{ __html: title }} />
      <p className={`text-sm leading-relaxed mb-4 ${featured ? 'text-base/85' : 'text-charcoal/70'}`} dangerouslySetInnerHTML={{ __html: body }} />
      <span className={`inline-flex items-center gap-1 text-sm font-semibold ${featured ? 'text-mustard' : 'text-heritage'}`}>
        Read more <ArrowUpRight size={13} />
      </span>
    </Link>
  );
}

/**
 * Larger "marketing channel" promo card used on the landing page. Pairs an
 * icon, kicker label, title, blurb and a Read-more CTA. Accepts either an
 * internal `to` path (Link) or an external `href` (anchor).
 */
function SectionCard({ icon: Icon, kicker, title, body, cta, to, href }) {
  const external = !!href;
  const Wrap = external ? 'a' : Link;
  const wrapProps = external
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : { to };
  const slug = (title || '').toLowerCase().replace(/[^a-z]+/g, '-').slice(0, 30);
  return (
    <Wrap
      {...wrapProps}
      data-testid={`section-card-${slug}`}
      className="block bg-base border border-charcoal/10 rounded-2xl p-7 md:p-8 hover:border-heritage hover:shadow-sm transition-all group"
    >
      <Icon size={28} className="text-heritage mb-4" />
      <div className="label text-charcoal/55 mb-2 text-[11px]" dangerouslySetInnerHTML={{ __html: kicker }} />
      <h3 className="font-display text-2xl mb-3 text-charcoal" dangerouslySetInnerHTML={{ __html: title }} />
      <p className="text-charcoal/75 text-sm leading-relaxed mb-5" dangerouslySetInnerHTML={{ __html: body }} />
      <span className="inline-flex items-center gap-1.5 text-heritage text-sm font-semibold group-hover:gap-2.5 transition-all">
        <span dangerouslySetInnerHTML={{ __html: cta }} /> <ArrowUpRight size={14} />
      </span>
    </Wrap>
  );
}
